import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { EvaluatedItemProps } from "@repo/ui";
import { cn } from "@repo/utils";
import { WorkflowsTableFooter } from "./workflows-table-footer";
import { WorkflowsTableHeader } from "./workflows-table-header";
import { WorkflowsTableRow } from "./worklows-table-row";

export type TableColumn<T> = {
  label: string;
  key: keyof T;
  render?: (value: T[keyof T], row: T) => ReactNode;
};

type Props<T> = {
  className?: string;
  columns: TableColumn<T>[];
  data: (T & { id: string | number })[];
  onClickAdd?: () => void;
  addButtonLabel?: string;
  onReorder?: (newData: T[], oldIndex?: number, newIndex?: number) => void;
  dropdownMenuItems?: EvaluatedItemProps[];
  isSortable?: boolean;
  selectedIndex?: number | null;
  onSelectRow?: (index: number | null) => void;
};

export const WorkflowsTable = <T extends object>({
  className,
  columns,
  data,
  onClickAdd,
  addButtonLabel,
  onReorder,
  dropdownMenuItems,
  isSortable = false,
  selectedIndex,
  onSelectRow,
}: Props<T>) => {
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">("bottom");

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const prevDataLengthRef = useRef(data.length);
  const isUserAddAction = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (data.length > prevDataLengthRef.current && isUserAddAction.current) {
      if (tableWrapperRef.current) {
        tableWrapperRef.current.scrollTo({
          top: tableWrapperRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
      onSelectRow?.(data.length - 1);
      setMenuPlacement("top");
      isUserAddAction.current = false;
    }
    prevDataLengthRef.current = data.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onReorder) {
      const oldIndex = data.findIndex((item) => item.id === active.id);
      const newIndex = data.findIndex((item) => item.id === over.id);
      const newArray = arrayMove(data, oldIndex, newIndex);
      onReorder(newArray, oldIndex, newIndex);
    }
  };

  const handleRowClick = (index: number, event: React.MouseEvent<HTMLTableRowElement>) => {
    onSelectRow?.(index);
    if (tableWrapperRef.current) {
      const containerRect = tableWrapperRef.current.getBoundingClientRect();
      const rowRect = event.currentTarget.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - rowRect.bottom;
      const MIN_SPACE_REQUIRED = 100;

      if (spaceBelow < MIN_SPACE_REQUIRED) setMenuPlacement("top");
      else setMenuPlacement("bottom");
    }
  };

  return (
    <div className={cn("flex flex-col justify-between rounded-sm bg-gray-100", className)}>
      <div
        data-allow-navigation
        className="custom-scrollbar overflow-auto px-7 pb-2"
        ref={tableWrapperRef}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="w-full border-collapse text-left">
            <WorkflowsTableHeader columns={columns} />
            <SortableContext
              items={data.map((d) => d.id)}
              disabled={!isSortable}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {data.map((row, index) => (
                  <WorkflowsTableRow
                    key={row.id}
                    row={row}
                    index={index}
                    columns={columns}
                    selectedRowIndex={selectedIndex ?? null}
                    handleRowClick={handleRowClick}
                    menuPlacement={menuPlacement}
                    dropdownMenuItems={dropdownMenuItems}
                  />
                ))}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </div>
      {onClickAdd && (
        <WorkflowsTableFooter
          addButtonLabel={addButtonLabel}
          onClickAdd={() => {
            isUserAddAction.current = true;
            onClickAdd?.();
          }}
          isSortable={isSortable}
        />
      )}
    </div>
  );
};
